<template>
  <div class="account-page-wrapper">
    <div class="account-main-content">
      <section>
        <h1 class="header">Create New Account</h1>

        <div v-if="customerError" class="customer-error">
          {{ customerError }}
        </div>
      </section>
      <section class="content">
        <!-- TODO - check for email in LS or something similar to let them know that they already have an accont -->
        <div v-if="!loggedIn" id="login-form-row" class="login-row flex-inline desk-hor-mob-col">
          <form class="create-account-form form" autocomplete="on" @submit.prevent="createNewCustomer()">
            <div class="solo-input-parent">
              <label for="frmEmailA" class="solo-label">
                {{ useTranslate('GLOBALS.emailAddress') }}
                <input
                  id="frmEmailA"
                  v-model="state.email"
                  class="solo-input"
                  type="email"
                  name="email"
                  placeholder="name@example.com"
                  required
                  :minlength="3"
                  :maxlength="255"
                  autocomplete="email"
                  aria-autocomplete="both"
                />
              </label>
            </div>

            <div class="solo-input-parent">
              <label for="frmEmailC" class="solo-label">
                Confirm {{ useTranslate('GLOBALS.emailAddress') }}
                <input
                  id="frmEmailC"
                  v-model="state.emailConfirm"
                  class="solo-input"
                  type="email"
                  name="email"
                  placeholder="name@example.com"
                  required
                  minlength="3"
                  maxlength="255"
                  autocomplete="email"
                  aria-autocomplete="both"
                />
              </label>
            </div>

            <div class="solo-input-parent">
              <label for="password" class="solo-label">
                {{ useTranslate('GLOBALS.password') }}
                <input
                  id="password"
                  v-model="state.password"
                  class="solo-input"
                  type="password"
                  name="password"
                  required
                  :placeholder="useTranslate('GLOBALS.password')"
                  autocomplete="new-password"
                  aria-autocomplete="both"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  minlength="8"
                  maxlength="36"
                />
              </label>
            </div>

            <div class="solo-input-parent">
              <label for="confirm-password" class="solo-label">
                {{ useTranslate('CreateAccount.confirmPassword') }}
                <input
                  id="confirm-password"
                  v-model="state.passwordConfirm"
                  class="solo-input"
                  type="password"
                  name="confirm-password"
                  required
                  :placeholder="useTranslate('CreateAccount.confirmPassword')"
                  autocomplete="new-password"
                  aria-autocomplete="both"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  minlength="8"
                  maxlength="36"
                />
              </label>
            </div>

            <div class="solo-input-parent">
              <label for="frName" class="solo-label">
                {{ useTranslate('CreateAccount.firstName') }}
                <input
                  id="frName"
                  v-model="state.firstName"
                  class="solo-input"
                  name="first-name"
                  required
                  :placeholder="useTranslate('CreateAccount.firstName')"
                  autocomplete="fname"
                  aria-autocomplete="both"
                />
              </label>
            </div>

            <div class="solo-input-parent">
              <label for="lName" class="solo-label">
                {{ useTranslate('CreateAccount.lastName') }}
                <input
                  id="lName"
                  v-model="state.lastName"
                  class="solo-input"
                  name="last-name"
                  required
                  :placeholder="useTranslate('CreateAccount.lastName')"
                  autocomplete="lname"
                  aria-autocomplete="both"
                />
              </label>
            </div>

            <Input
              v-model="state.consent"
              type="checkbox"
              name="Consent"
              :required="true"
              class="input-container-consent"
            >
              <!-- TODO - may need to separate -->
              <div class="flex-inline flex-wrap">
                Consent - I agree to the
                <a :href="localePath('/privacy-policy')" class="ul" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                <span style="margin-left: 0.3125rem">and</span>
                <a :href="localePath('/terms-conditions')" class="ul" target="_blank" rel="noopener noreferrer">
                  Terms & Conditions
                </a>
              </div>
            </Input>

            <div class="form-actions flex-col cntr">
              <!-- @click-button="createNewCustomer()" -->
              <CustomButton type="submit" :disabled="rootStore?.siteUnderMaintenance">{{
                useTranslate('LoginPage.createAccount')
              }}</CustomButton>
              <span class="">
                {{ useTranslate('CreateAccount.alreadyHaveAnAccountSignIn') }}
                <NuxtLink :to="localePath('/account')" class="sign-in ul"
                  >{{ useTranslate('LoginPage.signIn') }}.</NuxtLink
                >
              </span>
            </div>
          </form>
        </div>
        <div v-else>You're Already Logged In!</div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { metaGen } from '~/util/metadata';
import { useRootStore } from '~/store';
import { useCustomerStore } from '~/store/customer';
import { mapAccountDataToGtmEvent, GtmEventType } from '~/util/analytics';

type BCState = {
  country_id: number;
  id: number;
  state: string;
  state_abbreviation: string;
};
type BCCountry = {
  country: string;
  country_iso2: string;
  country_iso3: string;
  id: number;
};
type SoloInputVModel = {
  name: string;
  value: any;
};

const rootStore = useRootStore();
const customerStore = useCustomerStore();
const route = useRoute();
const localePath = useLocalePath();
const config = useRuntimeConfig();

useHead({
  ...(metaGen(rootStore?.brandSettings, {
    page_title: 'Create New Account',
  }) as any),
});

// const { data: asyncData, error: asyncError } = await useAsyncData(async() => {
//   const { regionHeaderName, regionLocaleProperties } = useRegionRequestSettings();
//   const { regionCode } = regionLocaleProperties.value;
//   const headers = {
//     [regionHeaderName]: regionCode,
//   };
//   const result = await $fetch<BCCountry[]>('/api/bc/get-all-countries', {
//     method: 'GET',
//     headers,
//   });
//   return {
//     countries: result,
//   };
// });

// useAsyncErrorHandling({
//   error: asyncError.value,
//   overrideError: {
//     statusCode: 300,
//     message: 'Error Fetching All Countries',
//   },
// });

const state = reactive({
  selectedCountry: {} as BCCountry,
  selectedState: {} as BCState,
  stateList: [] as BCState[],
  email: '',
  emailConfirm: '',
  password: '',
  passwordConfirm: '',
  firstName: '',
  lastName: '',
  address: '',
  city: {} as SoloInputVModel,
  zip: {} as SoloInputVModel,
  consent: {} as SoloInputVModel,
  phone: '',
});

const customerError = computed(() => customerStore.customerError);
const loggedIn = computed(() => customerStore.loggedIn);
const query = computed(() => route?.query);
const redirectUrl = computed<string | undefined>(() => (query as any)?.redirect as string);

watch(
  () => state.selectedCountry,
  (newCountry, oldCountry) => {
    if (newCountry?.id !== oldCountry?.id) {
      getStates(newCountry);
    }
  }
);

async function createNewCustomer() {
  // TODO - validate items (email/pass match etc)
  if (state.email === state.emailConfirm && state.password === state.passwordConfirm) {
    await useCreateCustomer({
      customerArray: [
        {
          email: state.email,
          last_name: state.lastName,
          first_name: state.firstName,
          authentication: {
            force_password_reset: false,
            new_password: state.password,
          },
          accepts_product_review_abandoned_cart_emails: true,
          original_channel_id: config.public.bcChannelId,
        },
      ],
      redirectToAccount: !redirectUrl.value,
    });

    // opt-in is currently always false - update with bool logic once there's
    // an opt-in to marketing emails option during account creation
    const accountInformation = {
      email: state.email,
      optIn: false,
      first_name: state.firstName,
      last_name: state.lastName,
    };
    const loginInfo = {
      email: state.email,
    };
    const gtm = useGtm();
    gtm?.trackEvent(mapAccountDataToGtmEvent(GtmEventType.accountCreation, accountInformation));
    gtm?.trackEvent(mapAccountDataToGtmEvent(GtmEventType.login, loginInfo));

    // if (redirectUrl.value) {
    //   await navigateTo({
    //     path: localePath(redirectUrl.value)
    //   });
    // }
  }
}
async function getStates(newCountry: any) {
  const { regionHeaderName, regionLocaleProperties } = useRegionRequestSettings();
  const { regionCode } = regionLocaleProperties.value;
  const headers = {
    [regionHeaderName]: regionCode,
  };
  const result = await $fetch<BCState[]>(`/api/bc/get-states?countryId=${newCountry?.id}`, { method: 'GET', headers });
  state.stateList = result;
}
// function formSubmitted(e: any) {
//   const formData = new FormData(e.target);

//   for (const [key, value] of formData.entries()) {
//     // this.$set(this.submittedForm, key, value); // replace
//   }
// };
</script>

<style lang="scss" scoped>
.account-page-wrapper {
  padding-top: 1rem;
}
.account-main-content {
  justify-content: space-between;
  margin: 0 auto;
  max-width: 66.66667rem;
  width: 100%;
  @include local-mixins.tablet_and_mobile {
    max-width: 100%;
  }
}
.content {
  justify-content: center;
  display: flex;
  .create-account-form {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    .solo-input-parent,
    .input-component-container,
    .g-recaptcha {
      width: 100%;
      margin-top: 1.25rem;
    }
    @mixin grid-form {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      .solo-input-parent,
      .input-component-container,
      .g-recaptcha {
        &:nth-child(2n-1) {
          margin-right: 0.625rem;
        }
        &:nth-child(2n) {
          margin-left: 0.625rem;
        }
      }
    }
    @include local-mixins.tablet {
      @include grid-form;
    }
    @include local-mixins.desktop {
      @include grid-form;
    }
  }
}
.form-actions {
  grid-column: 1 / span 2;
  width: 100%;
  margin-top: #{local-functions.rem-calc(40)};
  .btn {
    margin-bottom: 1rem;
  }
}
.header {
  text-align: center;
}
</style>

<style lang="scss" scoped>
// TODO - this is from input styles - remove
.input-component-container {
  box-sizing: border-box;
}

.customer-message,
.customer-error {
  padding: 0.75rem;
  width: 100%;
  border-radius: 5px;
  margin-bottom: 1rem;
}

.customer-error {
  border: 1px solid $color-danger-dark;
  background-color: $color-danger-light;
  color: $color-danger-dark;
}

.solo-label {
  color: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: left;
  text-transform: uppercase;
  width: 100%;
}

.solo-input {
  border: none;
  font-size: 1rem;
  height: 1.5rem;
  outline: none;
  padding: 0;
  width: 100%;

  &::-webkit-calendar-picker-indicator {
    display: none;
  }
}

textarea.solo-input {
  min-height: 30px;
  max-width: 100%;
  min-width: 100%;
}

.input-txt-danger {
  align-items: center;
  color: $color-danger-dark;
  display: flex;
  fill: $color-danger-dark;
  justify-content: flex-start;
  margin: 0.5rem 0;

  :deep(svg) {
    height: 1rem;
    width: 1rem;
    margin-right: 4px;
  }
}

.solo-input-parent {
  @include local-mixins.solo-input-parent;
  &.input-danger {
    &:hover {
      border: 2px solid $color-danger-dark;
    }

    &:focus-within {
      .solo-input {
        color: $color-neutral-cool-900;
      }
    }

    /* stylelint-disable-next-line no-descending-specificity */
    .solo-input {
      color: inherit;
    }
  }

  &:focus-within {
    /* stylelint-disable-next-line no-descending-specificity */
    .solo-input {
      &::placeholder {
        opacity: 0;
      }
    }

    &:hover {
      border: 2px solid $color-secondary-500;
      color: $color-secondary-500;
      fill: $color-secondary-500;
    }
  }
}
.input-container-consent {
  display: flex;
  align-items: center;
  grid-column: 1 / 3;
  &:deep() {
    .solo-input-parent {
      border: none;
    }
    .solo-label {
      display: flex;
      align-items: center;
    }
    .solo-input {
      order: -1;
      width: 1.5rem;
      margin-right: 0.625rem;
    }
    a {
      margin-left: #{local-functions.rem-calc(5)};
    }
  }
}
</style>
